import {
  Close,
  ExtensionRounded,
  FavoriteRounded,
  LayersRounded,
  WebRounded,
} from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogTitle,
  Grid,
  Tab,
  Tabs,
  Typography,
  alpha,
  Stack,
  IconButton,
} from "@mui/material";
import _ from "lodash";
import { nanoid } from "nanoid";
import * as React from "react";
import { useRecoilState, useResetRecoilState } from "recoil";
import { useSaveData } from "../hooks/useSaveData";
import { SELECTED_ELEMENT } from "../hooks/useSelectedElement";
import { isCommon, useXCommonChildren } from "../hooks/useXCommon";
import { USE_X_ELEMENT } from "../hooks/useXElement";
import { useXPage } from "../hooks/useXPage";
import { COMPONENT_READABLE_NAME } from "../utils/constants/common/componentName";
import { COMPONENT_CHILDREN } from "../utils/constants/gui/componentChildren";
import { XElementConfig } from "../utils/interfaces/Elements/XElement.interfaces";
import { TEMPLATES } from "../utils/templates";
import {
  TComponentName,
  TComponentType,
} from "../utils/types/common/components.types";
import { TTemplate } from "../utils/types/common/templates.types";

interface AddElementDialogProps {
  setOpen: (open: boolean) => void;
  isSelf?: boolean;
}

const AddElementDialog: React.FC<AddElementDialogProps> = ({
  setOpen,
  isSelf = false,
}) => {
  const saveData = useSaveData();

  const [selectedMainTab, setSelectedMainTab] = React.useState<number>(0);

  // FOR SETTING ELEMENT ON THE MAIN PAGE
  const { setXPage, xPage } = useXPage();

  // TODO IMPLEMENT FOR SETTING ELEMENT ON THE HEADER

  // FOR SETTING ELEMENT IN THE FOOTER

  const [selectedElement, setSelectedElement] =
    useRecoilState(SELECTED_ELEMENT);
  const { xChildren, setXChildren } = useXCommonChildren(
    selectedElement?.index ?? ("" as any)
  );
  const reset = useResetRecoilState(
    USE_X_ELEMENT(selectedElement ? selectedElement.index : "DEFAULT_ELEMENT")
  );

  const elements = selectedElement
    ? COMPONENT_CHILDREN[selectedElement.name]
    : COMPONENT_CHILDREN.XPage;

  const updateElement = async (template: TComponentType[]) => {
    /**
     * save the current pages data for safety
     */
    await saveData();

    // UPDATING CURRENT ELEMENT TEMPLATE
    const localIndex = nanoid(5);

    let prefix = isCommon(selectedElement?.index ?? "")
      ? selectedElement?.index
      : "";

    if (isSelf && selectedElement) {
      if (template.length === 1) {
        setSelectedElement({
          ...selectedElement,
          value: template[0].props,
        });
      } else {
        let reference: TComponentType[] = isCommon(selectedElement.index)
          ? xChildren
          : (xPage as TComponentType[]);

        const positionInPage = _.findIndex(reference, (section) => {
          if (!section.config) return false;

          return section.config.index === selectedElement.index;
        });

        const localTemplate: TComponentType[] = template.map((el) => {
          const config: XElementConfig = {
            ...el.config,
            index:
              prefix +
              (el.config ? el.config.index + "_" + localIndex : localIndex),
          };

          if (config.childOf) {
            config.childOf = prefix + (config.childOf + "_" + localIndex);
          } else {
            if (selectedElement.childOf) {
              config.childOf = selectedElement.childOf;
            }
          }

          return { ...el, config };
        });

        if (isCommon(selectedElement.index)) {
          setXChildren((old) => {
            const _newPage = [...old];

            _newPage.splice(positionInPage, 1, ...localTemplate);

            return _newPage;
          });
        } else {
          setXPage((old) => {
            const _newPage = [...(old as TComponentType[])];

            _newPage.splice(positionInPage, 1, ...localTemplate);

            return _newPage;
          });
        }

        reset();
      }
    }
    // ADDING CHILD ELEMENT
    else {
      const localTemplate: TComponentType[] = template.map((el) => {
        const config: XElementConfig = {
          ...el.config,
          index:
            prefix +
            (el.config ? el.config.index + "_" + localIndex : localIndex),
        };

        if (config.childOf) {
          config.childOf = prefix + (config.childOf + "_" + localIndex);
        } else {
          // IF IT IS NOT A PAGE ADD THIS TO THE CURRENT ELENENT
          if (selectedElement) config.childOf = selectedElement.index;
        }

        return { ...el, config };
      });

      if (localTemplate.length > 0) {
        if (selectedElement && isCommon(selectedElement.index)) {
          setXChildren((old) => [...old, ...localTemplate]);
        } else {
          setXPage((old) => [...(old as TComponentType[]), ...localTemplate]);
        }
      }
    }
  };

  return (
    <Dialog
      maxWidth="lg"
      open
      fullWidth
      onClose={() => setOpen(false)}
      PaperProps={{
        sx: {
          bgcolor: "#131519",
          color: "white",
          borderRadius: 3,
          height: "70vh",
        },
      }}
      componentsProps={{
        root: { style: { zIndex: 10000 } },
      }}
    >
      <DialogTitle
        sx={{
          pb: isSelf ? 2 : 0,
          pt: 2,
          fontSize: 14,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: 1,
          bgcolor: "#1F212B",
          alignItems: "center",
          display: "flex",
        }}
      >
        <LayersRounded sx={{ mr: 2 }} />
        <Typography component="span" sx={{ flex: 1 }}>
          Choisir un template
        </Typography>
        <IconButton color="error" size="small" onClick={() => setOpen(false)}>
          <Close />
        </IconButton>
      </DialogTitle>
      {!isSelf && (
        <Tabs
          sx={{
            bgcolor: "#1F212B",
            minHeight: 72,
            ".MuiTab-root": {
              color: "rgba(255,255,255,0.6)",

              "&.Mui-selected": {
                color: "secondary.main",
              },
            },
          }}
          variant="fullWidth"
          value={selectedMainTab}
          onChange={(_, value) => setSelectedMainTab(value)}
          textColor="secondary"
          indicatorColor="secondary"
        >
          <Tab
            label="Composants"
            icon={<ExtensionRounded />}
            iconPosition="start"
          />
          {!selectedElement && (
            <Tab
              label="Pages"
              icon={<WebRounded />}
              iconPosition="start"
              disabled
            />
          )}
          <Tab
            label="Sauvegardes"
            icon={<FavoriteRounded />}
            iconPosition="start"
            disabled
          />
        </Tabs>
      )}
      <SubTab
        elements={
          isSelf && selectedElement ? [selectedElement.name] : elements ?? []
        }
        updateElement={updateElement}
      />
    </Dialog>
  );
};

export default AddElementDialog;

interface SubTabProps {
  elements: TComponentName[];
  updateElement: (template: TComponentType[]) => void;
}

const SubTab: React.FC<SubTabProps> = ({ elements, updateElement }) => {
  const [selectedSubTab, setSelectedSubTab] = React.useState<TComponentName>(
    elements[0]
  );

  const templates: TTemplate = selectedSubTab
    ? TEMPLATES[selectedSubTab] ?? {}
    : {};

  return (
    <Grid container spacing={2} padding={2} height="calc(100% - 72px)">
      <Grid item width={200} sx={{ overflowY: "scroll", height: "100%" }}>
        <Tabs
          variant="scrollable"
          orientation="vertical"
          textColor="inherit"
          value={selectedSubTab}
          onChange={(_, value) => setSelectedSubTab(value)}
          sx={{
            ".MuiTab-root": {
              minHeight: "auto",
              fontSize: 12,
              alignItems: "flex-start",

              "&.Mui-selected": {
                bgcolor: "#1F212B",
              },
            },
          }}
        >
          {elements.map((element) => (
            <Tab
              key={element}
              value={element}
              label={COMPONENT_READABLE_NAME[element]}
            />
          ))}
        </Tabs>
      </Grid>
      <Grid item xs sx={{ overflowY: "scroll", height: "100%" }}>
        {Object.keys(templates).map((templateName) => (
          <TemplatePreview
            key={templateName}
            element={templates[templateName] as any}
            templateName={templateName}
            updateElement={updateElement}
          />
        ))}
      </Grid>
    </Grid>
  );
};

const TemplatePreview: React.FC<{
  element: { image?: string; template: TComponentType[] };
  templateName: string;
  updateElement: (template: TComponentType[]) => void | Promise<void>;
}> = ({ element, templateName, updateElement }) => {
  return (
    <Stack
      minHeight={150}
      bgcolor="#1F212B"
      justifyContent="flex-end"
      position="relative"
      overflow="hidden"
      sx={{ "&:not(:last-of-type)": { mb: 2 }, border: "5px solid #0D0E11" }}
    >
      {element.image && (
        <img
          src={element.image}
          alt={templateName}
          style={{ width: "100%" }}
          loading="lazy"
        />
      )}
      {/* {displayComponent(template.component)} */}
      <Stack
        height="calc(100% - 50px)"
        width="100%"
        direction="row"
        className="hover-content"
        justifyContent="center"
        alignItems={element.image ? "center" : "flex-start"}
        spacing={1}
        position="absolute"
        top={0}
        left={0}
        sx={{
          backdropFilter: "blur(0px)",
          transition: "all 0.2s linear",
          bgcolor: alpha("#1F212B", 0),

          "& button": {
            opacity: 0,
          },

          "&:hover": {
            bgcolor: alpha("#1F212B", 0.5),
            backdropFilter: "blur(5px)",

            "& button": {
              opacity: 1,
            },
          },
        }}
      >
        <Button
          variant="contained"
          color="primary"
          sx={{
            height: 40,
            borderRadius: 1.5,
            fontSize: 12,
            letterSpacing: 1,
            mt: element.image ? 0 : 3.5,
          }}
          size="small"
          onClick={async () => await updateElement(element.template)}
        >
          Ajouter
        </Button>
        {/* <IconButton size="small" color="secondary" title="Ajouter aux Favoris">
          {inFavories ? <BookmarkRounded /> : <BookmarkBorderRounded />}
        </IconButton> */}
      </Stack>
      <Typography
        sx={{
          // position: "absolute",
          // bottom: 0,
          // left: -8,
          height: 50,
          py: 1,
          px: 2,
          fontSize: 24,
          bgcolor: "#0D0E11",
          width: "100%",
          fontWeight: 700,
          letterSpacing: 0.5,
        }}
      >
        {templateName}
      </Typography>
    </Stack>
  );
};
